export type LogLevel = "info" | "warn" | "error"

type LogMetadata = Record<string, unknown>

function emitServerPostHogLog(level: LogLevel, event: string, metadata: LogMetadata) {
  if (typeof window !== "undefined") {
    return
  }

  void import("@/lib/posthog/logs")
    .then(({ emitServerPostHogLog }) => {
      emitServerPostHogLog(level, event, metadata)
    })
    .catch(() => undefined)
}

async function captureServerPostHogException(error: unknown, metadata: LogMetadata) {
  if (typeof window !== "undefined") {
    return
  }

  try {
    const { captureServerPostHogException } = await import("@/lib/posthog/server")
    await captureServerPostHogException(error, metadata)
  } catch {
    return
  }
}

function toJsonSafeValue(value: unknown, depth = 0): unknown {
  if (depth > 5) {
    return "[MaxDepthExceeded]"
  }

  if (value === null || value === undefined) {
    return value
  }

  if (typeof value === "bigint") {
    return value.toString()
  }

  if (value instanceof Error) {
    return getErrorMetadata(value)
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (Array.isArray(value)) {
    return value.map((item) => toJsonSafeValue(item, depth + 1))
  }

  if (typeof value === "object") {
    const safeRecord: Record<string, unknown> = {}

    for (const [key, nestedValue] of Object.entries(value)) {
      safeRecord[key] = toJsonSafeValue(nestedValue, depth + 1)
    }

    return safeRecord
  }

  if (typeof value === "function") {
    return "[Function]"
  }

  return value
}

function toJsonSafeMetadata(metadata: LogMetadata) {
  return toJsonSafeValue(metadata) as LogMetadata
}

export function logEvent(scope: string, level: LogLevel, event: string, metadata: Record<string, unknown>, errorForPostHog?: unknown) {
  const safeMetadata = toJsonSafeMetadata(metadata)
  const payload = JSON.stringify({
    scope,
    level,
    event,
    ...safeMetadata,
  })
  emitServerPostHogLog(level, event, {
    scope,
    event,
    ...safeMetadata,
  })

  switch (level) {
    case "error":
      console.error(payload)
      return captureServerPostHogException(errorForPostHog ?? new Error(typeof safeMetadata.errorMessage === "string" ? safeMetadata.errorMessage : event), {
        scope,
        event,
        ...safeMetadata,
      })
    case "warn":
      console.warn(payload)
      return
    case "info":
    default:
      console.info(payload)
      return
  }
}

export function createScopedLogger(scope: string, baseMetadata: LogMetadata = {}) {
  const log = (level: LogLevel, event: string, metadata: LogMetadata = {}) => {
    logEvent(scope, level, event, {
      ...baseMetadata,
      ...metadata,
    })
  }

  return {
    info(event: string, metadata: LogMetadata = {}) {
      log("info", event, metadata)
    },
    warn(event: string, metadata: LogMetadata = {}) {
      log("warn", event, metadata)
    },
    error(event: string, errorOrMetadata: unknown, metadata: LogMetadata = {}) {
      if (errorOrMetadata instanceof Error || typeof errorOrMetadata === "string") {
        return logEvent(
          scope,
          "error",
          event,
          {
            ...baseMetadata,
            ...metadata,
            ...getErrorMetadata(errorOrMetadata),
          },
          errorOrMetadata
        )
        return
      }

      if (errorOrMetadata && typeof errorOrMetadata === "object") {
        return logEvent(
          scope,
          "error",
          event,
          {
            ...baseMetadata,
            ...(errorOrMetadata as LogMetadata),
            ...metadata,
          },
          new Error(event)
        )
        return
      }

      return logEvent(
        scope,
        "error",
        event,
        {
          ...baseMetadata,
          ...metadata,
        },
        new Error(event)
      )
    },
    child(metadata: LogMetadata) {
      return createScopedLogger(scope, {
        ...baseMetadata,
        ...metadata,
      })
    },
  }
}

export async function withLoggedTiming<T>({
  logger,
  startEvent,
  successEvent,
  failureEvent,
  metadata = {},
  run,
}: {
  logger: ReturnType<typeof createScopedLogger>
  startEvent?: string
  successEvent: string
  failureEvent: string
  metadata?: LogMetadata
  run: () => Promise<T>
}) {
  const startedAt = Date.now()

  if (startEvent) {
    logger.info(startEvent, metadata)
  }

  try {
    const result = await run()

    logger.info(successEvent, {
      ...metadata,
      durationMs: Date.now() - startedAt,
    })

    return result
  } catch (error) {
    await logger.error(failureEvent, error, {
      ...metadata,
      durationMs: Date.now() - startedAt,
    })
    throw error
  }
}

export function getErrorMetadata(error: unknown) {
  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message,
      ...(error.stack ? { errorStack: error.stack } : {}),
    }
  }

  if (typeof error === "string") {
    return {
      errorMessage: error,
    }
  }

  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>
    const errorMessage = typeof record.message === "string" ? record.message : undefined
    const errorName = typeof record.name === "string" ? record.name : undefined

    return {
      ...(errorName ? { errorName } : {}),
      ...(errorMessage ? { errorMessage } : {}),
    }
  }

  return {
    errorMessage: "Unknown error",
  }
}
